/**
 * ProfitPulse Widget (MA-BE-082)
 * 
 * Executive profit dashboard widget for War Room.
 * 
 * Displays:
 * - Total Profit (primary metric)
 * - Capital Efficiency (ROAS equivalent) with confidence indicator
 * - Capital Deployed (ad spend) with confidence indicator
 * - Total Revenue
 * - Total Commission
 * 
 * Metric Confidence Standard:
 * - exact: Computed from canonical time-series data
 * - estimated: Computed using summary or inferred inputs
 * - missing_data: Metric cannot be computed
 * - not_applicable: Metric not relevant for current filter
 * 
 * When ad spend data is unavailable, shows "missing_ad_spend" status
 * and displays null for capital-related metrics.
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { TrendingUp, TrendingDown, DollarSign, Percent, Activity, AlertTriangle, Info, AlertCircle } from 'lucide-react';
import { useProfitPulse } from '@/hooks/useWarRoomMetrics';

// Status color mapping
const statusColors = {
  excellent: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  good: 'bg-green-500/20 text-green-300 border-green-500/30',
  break_even: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  negative: 'bg-red-500/20 text-red-300 border-red-500/30',
  missing_ad_spend: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
};

const statusLabels = {
  excellent: '✓ Excellent (3x+ ROAS)',
  good: '✓ Good (2x+ ROAS)',
  break_even: '⚠ Break Even',
  negative: '✗ Negative ROI',
  missing_ad_spend: '— No Ad Spend Data'
};

const statusIcons = {
  excellent: TrendingUp,
  good: TrendingUp,
  break_even: Activity,
  negative: TrendingDown,
  missing_ad_spend: AlertTriangle
};

// Confidence status indicator colors
const confidenceColors = {
  exact: 'text-emerald-400',
  estimated: 'text-amber-400',
  partial_data: 'text-orange-400',
  missing_data: 'text-slate-500',
  not_applicable: 'text-slate-500'
};

const confidenceLabels = {
  exact: 'Exact',
  estimated: 'Estimated',
  partial_data: 'Partial Data',
  missing_data: 'No Data',
  not_applicable: 'N/A'
};

// Currency formatter
const formatCurrency = (value, compact = false) => {
  if (value === null || value === undefined) return '—';
  
  if (compact && Math.abs(value) >= 1000) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(value);
  }
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Format efficiency as multiplier (e.g., 3.6x)
const formatEfficiency = (value) => {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(1)}x`;
};

/**
 * Confidence Indicator component
 */
const ConfidenceIndicator = ({ status, reason }) => {
  if (!status || status === 'exact') return null;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <AlertCircle className={`h-3 w-3 ml-1 ${confidenceColors[status]} cursor-help`} />
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-slate-900 border-slate-700 text-xs max-w-xs">
          <p className="font-medium">{confidenceLabels[status]}</p>
          {reason && <p className="text-slate-400 mt-1">{reason}</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export function ProfitPulse({ period = 'mtd', campaignId, funnelId, className = '' }) {
  const {
    loading,
    error,
    totalProfit,
    capitalDeployed,
    capitalEfficiency,
    capitalEfficiencyStatus,
    totalRevenue,
    totalCommission,
    hasCapitalData,
    capitalDeployedConfidence,
    capitalEfficiencyConfidence,
    capitalDeployedReason,
    capitalEfficiencyReason,
    isEstimated
  } = useProfitPulse({ period, campaignId, funnelId });
  
  if (loading) {
    return (
      <Card className={`glass border-white/10 ${className}`} data-testid="profit-pulse-loading">
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-24">
            <Activity className="h-6 w-6 text-slate-400 animate-pulse" />
            <span className="ml-2 text-slate-400">Loading Profit Pulse...</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // MA-PHASE11A-WARROOM-FINAL-VALIDATION-021: Neutral error state
  if (error) {
    return (
      <Card className={`glass border-white/10 ${className}`} data-testid="profit-pulse-error">
        <CardContent className="p-6">
          <div className="flex flex-col items-center text-center">
            <TrendingUp className="h-10 w-10 text-slate-600 mb-3" />
            <span className="text-slate-300 font-medium">Profit Pulse Unavailable</span>
            <span className="text-slate-500 text-sm mt-1">Unable to load profit metrics at this time.</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const StatusIcon = statusIcons[capitalEfficiencyStatus] || Activity;
  const statusColor = statusColors[capitalEfficiencyStatus] || statusColors.missing_ad_spend;
  const statusLabel = statusLabels[capitalEfficiencyStatus] || statusLabels.missing_ad_spend;
  
  return (
    <Card className={`glass border-white/10 ${className}`} data-testid="profit-pulse">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold text-slate-100 flex items-center gap-2">
            <Activity className="h-5 w-5 text-emerald-400" />
            Profit Pulse
          </CardTitle>
          <Badge className={`text-xs border ${statusColor}`} data-testid="profit-pulse-status">
            {statusLabel}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Primary Metric: Total Profit */}
        <div className="text-center py-4 border-b border-white/5">
          <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Total Profit</p>
          <p 
            className={`text-4xl font-bold ${totalProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}
            data-testid="profit-pulse-total-profit"
          >
            {formatCurrency(totalProfit)}
          </p>
        </div>
        
        {/* Secondary Metrics Grid */}
        <div className="grid grid-cols-2 gap-4">
          {/* Capital Efficiency */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Percent className="h-3 w-3 text-slate-400" />
              <p className="text-xs text-slate-400 uppercase tracking-wide">Capital Efficiency</p>
              <ConfidenceIndicator status={capitalEfficiencyConfidence} reason={capitalEfficiencyReason} />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3 w-3 text-slate-500 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-slate-900 border-slate-700 text-xs max-w-xs">
                    Profit divided by ad spend. Values above 1.0x indicate positive ROI.
                    {!hasCapitalData && (
                      <p className="mt-1 text-amber-400">No ad spend data available for calculation.</p>
                    )}
                    {isEstimated && (
                      <p className="mt-1 text-amber-400">Using campaign summary data (estimated).</p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p 
              className={`text-xl font-bold ${
                capitalEfficiency === null ? 'text-slate-500' :
                capitalEfficiency >= 2 ? 'text-emerald-400' :
                capitalEfficiency >= 1 ? 'text-amber-400' : 'text-red-400'
              }`}
              data-testid="profit-pulse-capital-efficiency"
            >
              {formatEfficiency(capitalEfficiency)}
            </p>
          </div>
          
          {/* Capital Deployed */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <DollarSign className="h-3 w-3 text-slate-400" />
              <p className="text-xs text-slate-400 uppercase tracking-wide">Capital Deployed</p>
              <ConfidenceIndicator status={capitalDeployedConfidence} reason={capitalDeployedReason} />
            </div>
            <p 
              className={`text-xl font-bold ${capitalDeployed === null ? 'text-slate-500' : 'text-slate-100'}`}
              data-testid="profit-pulse-capital-deployed"
            >
              {formatCurrency(capitalDeployed, true)}
            </p>
          </div>
        </div>
        
        {/* Revenue & Commission */}
        <div className="grid grid-cols-2 gap-4 pt-2 border-t border-white/5">
          <div className="text-center">
            <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Total Revenue</p>
            <p className="text-lg font-semibold text-slate-100" data-testid="profit-pulse-total-revenue">
              {formatCurrency(totalRevenue, true)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Commission</p>
            <p className="text-lg font-semibold text-slate-100" data-testid="profit-pulse-commission">
              {formatCurrency(totalCommission, true)}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Compact version for inline display
 * MA-IMPL-118: Added campaignId support
 */
export function ProfitPulseCompact({ period = 'mtd', campaignId, className = '' }) {
  const { totalProfit, capitalEfficiency, capitalEfficiencyStatus, loading } = useProfitPulse({ period, campaignId });
  
  if (loading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Activity className="h-4 w-4 text-slate-400 animate-pulse" />
      </div>
    );
  }
  
  const statusColor = statusColors[capitalEfficiencyStatus] || statusColors.missing_ad_spend;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className={`flex items-center gap-3 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 cursor-help ${className}`}
            data-testid="profit-pulse-compact"
          >
            <div className="flex items-center gap-1">
              <DollarSign className={`h-4 w-4 ${totalProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`} />
              <span className={`text-sm font-bold ${totalProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {formatCurrency(totalProfit, true)}
              </span>
            </div>
            {capitalEfficiency !== null && (
              <Badge className={`text-xs border ${statusColor}`}>
                {formatEfficiency(capitalEfficiency)}
              </Badge>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="bg-slate-900 border-slate-700">
          <p className="text-sm">Profit Pulse: {formatCurrency(totalProfit)}</p>
          {capitalEfficiency !== null && (
            <p className="text-xs text-slate-400">Capital Efficiency: {formatEfficiency(capitalEfficiency)}</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default ProfitPulse;
